/**
 * IndexedDB wrapper for simplified database operations
 */

const DB_NAME = 'legere-db';
const DB_VERSION = 1;

export interface DBSchema {
  books: {
    key: string;
    value: any;
    indexes: {
      createdAt: Date;
      lastReadAt?: Date;
    };
  };
  readingPositions: {
    key: string;
    value: any;
  };
  bookmarks: {
    key: string;
    value: any;
    indexes: {
      bookId: string;
      createdAt: Date;
    };
  };
}

export class IndexedDBWrapper {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private async getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create books store
        if (!db.objectStoreNames.contains('books')) {
          const booksStore = db.createObjectStore('books', { keyPath: 'id' });
          booksStore.createIndex('createdAt', 'createdAt', { unique: false });
          booksStore.createIndex('lastReadAt', 'lastReadAt', { unique: false });
        }

        // Create reading positions store
        if (!db.objectStoreNames.contains('readingPositions')) {
          db.createObjectStore('readingPositions', { keyPath: 'bookId' });
        }

        // Create bookmarks store
        if (!db.objectStoreNames.contains('bookmarks')) {
          const bookmarksStore = db.createObjectStore('bookmarks', { keyPath: 'id' });
          bookmarksStore.createIndex('bookId', 'bookId', { unique: false });
          bookmarksStore.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
    });

    return this.dbPromise;
  }

  async get<T>(storeName: string, key: string): Promise<T | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? this.deserialize(result) : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAll<T>(storeName: string): Promise<T[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const results = request.result || [];
        resolve(results.map(item => this.deserialize(item)));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName: string, value: any): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const serialized = this.serialize(value);
      const request = store.put(serialized);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName: string, key: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async count(storeName: string): Promise<number> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getByIndex<T>(
    storeName: string,
    indexName: string,
    value: any
  ): Promise<T[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => {
        const results = request.result || [];
        resolve(results.map(item => this.deserialize(item)));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteByIndex(
    storeName: string,
    indexName: string,
    value: any
  ): Promise<void> {
    const items = await this.getByIndex<any>(storeName, indexName, value);
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      let completed = 0;
      const total = items.length;

      if (total === 0) {
        resolve();
        return;
      }

      for (const item of items) {
        const request = store.delete(item.id || item.bookId);
        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      }
    });
  }

  private serialize(value: any): any {
    // IndexedDB can store ArrayBuffer, Blob, Date, etc. natively
    // No serialization needed - just return the value as is
    return value;
  }

  private deserialize(value: any): any {
    // IndexedDB returns data in correct format
    // We only need to restore Date objects
    if (!value || typeof value !== 'object') {
      return value;
    }

    return this.restoreDates(value);
  }

  private restoreDates(obj: any): any {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.restoreDates(item));
    }

    // Don't traverse ArrayBuffer or Blob
    if (obj instanceof ArrayBuffer || obj instanceof Blob || obj instanceof Uint8Array) {
      return obj;
    }

    // If already a Date, return as is
    if (obj instanceof Date) {
      return obj;
    }

    // Restore dates in object
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Check if this field should be a Date
      const isDateField = key === 'createdAt' || key === 'updatedAt' || key === 'lastReadAt' || key === 'timestamp';

      if (isDateField) {
        if (value instanceof Date) {
          // Already a Date
          result[key] = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
          // Convert string or timestamp to Date
          result[key] = new Date(value);
        } else {
          result[key] = value;
        }
      } else if (value && typeof value === 'object') {
        result[key] = this.restoreDates(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  async estimateQuota(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { usage: 0, quota: 0 };
  }
}
